import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useEffect, useState } from 'react';

import {
  ConnectServiceDialog,
  useConnectionServices,
} from './connect-service-dialog';
import { connectionApi } from './connection-api';
import type { ConnectionServiceDefinition } from './types';
import { useConnections } from './use-connections';

function RotationForm({
  workspaceId,
  connection,
  onRotated,
}: {
  workspaceId: string;
  connection: import('./types').WorkspaceConnectionSummary;
  onRotated: (connection: import('./types').WorkspaceConnectionSummary) => void;
}) {
  const t = useI18n();
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const requiredComplete =
    connection.serviceDefinition.connectionDefinition.setupFields.every(
      field => !field.required || Boolean(fields[field.name]?.trim())
    );
  return (
    <details>
      <summary>{t['com.streamlain.connections.reconnect']()}</summary>
      {connection.serviceDefinition.connectionDefinition.setupFields.map(
        field => (
          <label key={field.name}>
            {field.label}
            <input
              type={field.secret ? 'password' : 'text'}
              autoComplete="off"
              value={fields[field.name] ?? ''}
              onChange={event =>
                setFields(current => ({
                  ...current,
                  [field.name]: event.target.value,
                }))
              }
            />
          </label>
        )
      )}
      <Button
        variant="secondary"
        disabled={saving || !requiredComplete}
        onClick={() => {
          setSaving(true);
          setError(undefined);
          void connectionApi
            .rotate(workspaceId, connection.id, {
              fields,
              expectedVersion: connection.version,
            })
            .then(updated => {
              setFields({});
              onRotated(updated);
            })
            .catch(cause =>
              setError(
                cause instanceof Error
                  ? cause.message
                  : t['com.streamlain.connections.operation-failed']()
              )
            )
            .finally(() => setSaving(false));
        }}
      >
        {saving
          ? t['com.streamlain.connections.saving']()
          : t['com.streamlain.connections.save-credential']()}
      </Button>
      {error ? <p role="alert">{error}</p> : null}
    </details>
  );
}

function LabelForm({
  workspaceId,
  connection,
  onUpdated,
}: {
  workspaceId: string;
  connection: import('./types').WorkspaceConnectionSummary;
  onUpdated: (connection: import('./types').WorkspaceConnectionSummary) => void;
}) {
  const t = useI18n();
  const [label, setLabel] = useState(connection.label);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        if (!label.trim() || saving) return;
        setSaving(true);
        setError(undefined);
        void connectionApi
          .update(workspaceId, connection.id, {
            label: label.trim(),
            expectedVersion: connection.version,
          })
          .then(onUpdated)
          .catch(cause =>
            setError(
              cause instanceof Error
                ? cause.message
                : t['com.streamlain.connections.operation-failed']()
            )
          )
          .finally(() => setSaving(false));
      }}
    >
      <label>
        {t['com.streamlain.connections.connection-name']()}
        <input value={label} onChange={event => setLabel(event.target.value)} />
      </label>
      <Button
        type="submit"
        variant="secondary"
        disabled={!label.trim() || saving}
      >
        {saving
          ? t['com.streamlain.connections.saving']()
          : t['com.streamlain.connections.rename']()}
      </Button>
      {error ? <p role="alert">{error}</p> : null}
    </form>
  );
}

export function ConnectionSettingsSection({
  workspaceId,
  readOnly,
  canUse = !readOnly,
}: {
  readonly workspaceId: string;
  readonly readOnly: boolean;
  readonly canUse?: boolean;
}) {
  const t = useI18n();
  const connections = useConnections(workspaceId);
  const catalog = useConnectionServices(workspaceId);
  const [selectedService, setSelectedService] =
    useState<ConnectionServiceDefinition>();
  const [actionError, setActionError] = useState<string>();
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' || navigator.onLine
  );
  const healthLabel = (health: import('./types').ConnectionHealth) => {
    switch (health) {
      case 'healthy':
        return t['com.streamlain.connections.health.healthy']();
      case 'degraded':
        return t['com.streamlain.connections.health.degraded']();
      case 'revoked':
        return t['com.streamlain.connections.health.revoked']();
      case 'expired':
        return t['com.streamlain.connections.health.expired']();
      default:
        return t['com.streamlain.connections.health.unknown']();
    }
  };
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return (
    <section aria-labelledby="business-connections-title">
      <h2 id="business-connections-title">
        {t['com.streamlain.connections.title']()}
      </h2>
      <p>{t['com.streamlain.connections.description']()}</p>
      {!online ? (
        <p role="status">
          {t['com.streamlain.connections.offline-readonly']()}
        </p>
      ) : null}
      {connections.forbidden ? (
        <p role="alert">
          {t['com.streamlain.connections.no-view-permission']()}
        </p>
      ) : null}
      {connections.error ? (
        <p role="alert">
          {connections.error}{' '}
          <Button variant="secondary" onClick={() => void connections.reload()}>
            {t['com.streamlain.connections.retry']()}
          </Button>
        </p>
      ) : null}
      {connections.loading ? (
        <p role="status">{t['com.streamlain.connections.loading']()}</p>
      ) : null}
      {!connections.loading && connections.items.length === 0 ? (
        <p>{t['com.streamlain.connections.empty']()}</p>
      ) : null}
      <ul>
        {connections.items.map(connection => (
          <li key={connection.id}>
            <strong>{connection.label}</strong> ·{' '}
            {connection.serviceDefinition.name} ·{' '}
            {healthLabel(connection.healthStatus)}
            {connection.externalAccountLabel
              ? ` · ${connection.externalAccountLabel}`
              : null}
            <p>
              {t['com.streamlain.connections.scope']()}:{' '}
              {connection.serviceDefinition.connectionDefinition.capabilities.join(
                ', '
              )}
            </p>
            {!readOnly && online ? (
              <LabelForm
                workspaceId={workspaceId}
                connection={connection}
                onUpdated={connections.replace}
              />
            ) : null}
            <Button
              variant="secondary"
              disabled={!canUse || !online}
              onClick={() => {
                setActionError(undefined);
                void connectionApi
                  .test(workspaceId, connection.id)
                  .then(connections.reload)
                  .catch(cause =>
                    setActionError(
                      cause instanceof Error
                        ? cause.message
                        : t['com.streamlain.connections.operation-failed']()
                    )
                  );
              }}
            >
              {t['com.streamlain.connections.test']()}
            </Button>
            {!readOnly && online ? (
              <RotationForm
                workspaceId={workspaceId}
                connection={connection}
                onRotated={connections.replace}
              />
            ) : null}
            <Button
              variant="secondary"
              disabled={readOnly || !online}
              onClick={() => {
                setActionError(undefined);
                void connectionApi
                  .remove(workspaceId, connection.id)
                  .then(() => connections.remove(connection.id))
                  .catch(cause =>
                    setActionError(
                      cause instanceof Error
                        ? cause.message
                        : t['com.streamlain.connections.operation-failed']()
                    )
                  );
              }}
            >
              {t['com.streamlain.connections.delete']()}
            </Button>
          </li>
        ))}
      </ul>
      {actionError ? <p role="alert">{actionError}</p> : null}
      {connections.nextCursor ? (
        <Button
          variant="secondary"
          disabled={connections.loadingMore}
          onClick={() => void connections.loadMore()}
        >
          {connections.loadingMore
            ? t['com.streamlain.connections.loading-more']()
            : t['com.streamlain.connections.load-more']()}
        </Button>
      ) : null}
      <label>
        {t['com.streamlain.connections.service']()}
        <select
          disabled={!canUse || !online || catalog.loading}
          value={selectedService?.id ?? ''}
          onChange={event =>
            setSelectedService(
              catalog.services.find(
                service => service.id === event.target.value
              )
            )
          }
        >
          <option value="">
            {catalog.loading
              ? t['com.streamlain.connections.loading-more']()
              : t['com.streamlain.connections.connect-service']()}
          </option>
          {catalog.services.map(service => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
        </select>
      </label>
      {catalog.error ? (
        <p role="alert">
          {catalog.error}{' '}
          <Button variant="secondary" onClick={() => void catalog.reload()}>
            {t['com.streamlain.connections.retry']()}
          </Button>
        </p>
      ) : null}
      {selectedService ? (
        <ConnectServiceDialog
          open={true}
          workspaceId={workspaceId}
          service={selectedService}
          readOnly={!canUse}
          onClose={() => setSelectedService(undefined)}
          onConnected={connection => {
            connections.prepend(connection);
            setSelectedService(undefined);
          }}
        />
      ) : null}
    </section>
  );
}
